import { Controller } from "@hotwired/stimulus";

// Custom Listing sidebar results (no fetch):
// - manages list pagination UI state (page, replace vs append)
// - triggers api-sync refresh for "listings_search"
// - preview/details: sets listing_id and triggers api-sync refresh for "listing_details"
// - optionally prefills custom listing form from last listing_details response
export default class extends Controller {
  static targets = [
    "items",
    "listSpinner",
    "sentinel",
    "loadingMessage",
    "emptyMessage",
    "count",
    "countSpinner",
    "profileForm",
    "simulatorForm",
    "searchForm",
    "pageField",
    "listingId",
    "overrideFlag",
    "listPanel",
    "filtersOverlay",
    "sourceSelect",
    "programSelect",
    "programGroup",
    "locationGroup",
    "detailsEmptyState",
    "pinnedSection",
    "pinnedNotFound",
    "pinnedSeparator",
  ];
  static values = {
    debounce: { type: Number, default: 300 },
    programsUrl: String,
    programSelected: { type: Boolean, default: false },
  };

  connect() {
    this.observer = null;
    this.hasMore = true;
    this.pendingPrefill = false;
    this.loadingSearch = false;
    this.userHasSelected = false;
    this.activeListingId = null;
    // Id du bien épinglé courant (#295), mis à jour à chaque recherche.
    this.pinnedListingId = null;
    // Au chargement, si le Catalogue est déjà le panneau actif (utilisateur
    // ayant accès au catalogue), on programme la sélection automatique du
    // premier bien dès que la première recherche aura répondu. On réutilise le
    // mécanisme existant (pendingAutoSelectFirst + reconcileSelectionAfterSearch
    // + selectFirstListingIfNeeded), identique au clic sur l'onglet Catalogue
    // (évènement listing-browser:list-shown). Conditions inchangées : on ne
    // sélectionne que si l'utilisateur n'a pas fait de sélection manuelle, et la
    // sélection est différée tant que la liste n'est pas rendue.
    this.pendingAutoSelectFirst = (this.currentBrowserMode() === "list");
    this.lastSearchTotal = null;
    // Tant que la première page (page=1) n'a pas été chargée avec succès, on
    // interdit le scroll infini. Sinon, au chargement la sentinelle (« calcul
    // des efforts d'épargne… ») est visible dans une liste encore vide, ce qui
    // déclenche immédiatement l'IntersectionObserver -> loadMore() -> page=2,
    // et la requête de recherche initiale partait alors en page 2 (offset 50)
    // au lieu de la page 1. Cf. #272 (symptôme « page=2 au rechargement »).
    this.initialLoadDone = false;
    this.sentinelVisible = false;
    this.setupInfiniteScroll();
    // Snapshot the initial server-rendered HTML of the details column so we can
    // restore it (zeros / "-" placeholders) when the selected listing disappears
    // after a search re-render. Captured before any remote-bind mutation since
    // api-sync's initial listing_details fetch is debounced.
    this.snapshotDetailsColumn();

    this.onRemoteSuccess = (e) => this.handleRemoteSuccess(e);
    this.element.addEventListener("remote:update:success", this.onRemoteSuccess);
    this.onRemoteStart = (e) => this.handleRemoteStart(e);
    this.onRemoteFinish = (e) => this.handleRemoteFinish(e);
    this.element.addEventListener("remote:update:start", this.onRemoteStart);
    this.element.addEventListener("remote:update:finish", this.onRemoteFinish);

    // Après chaque (re)rendu de l'onglet « Favoris » (favorites_controller#
    // renderFavorites), on réapplique la surbrillance du bien actif sur les
    // cards favoris fraîchement insérées dans le DOM (#353).
    this.onFavoritesRendered = () => this.handleFavoritesRendered();
    this.element.addEventListener("favorites:rendered", this.onFavoritesRendered);

    // When profile / filters change, reset pagination to page=1 (replace)
    this.onPotentialFilterChange = (e) => this.handlePotentialFilterChange(e);
    this.bindResetOnInputs();

    // Listen for user modifications on custom listing form to set override flag
    this.onSimulatorInput = (e) => this.handleSimulatorInput(e);
    this.bindSimulatorInputs();

    // Initialize dependent selects if prefilled
    try { this.sourceChanged(); } catch (_) {}
    try { this.applyProgramSelectedState(); } catch (_) {}
  }

  disconnect() {
    if (this.observer) {
      try { this.observer.disconnect(); } catch (_) {}
    }
    try { this.element.removeEventListener("remote:update:success", this.onRemoteSuccess); } catch (_) {}
    try { this.element.removeEventListener("remote:update:start", this.onRemoteStart); } catch (_) {}
    try { this.element.removeEventListener("remote:update:finish", this.onRemoteFinish); } catch (_) {}
    try { this.element.removeEventListener("favorites:rendered", this.onFavoritesRendered); } catch (_) {}
    this.unbindResetOnInputs();
    this.unbindSimulatorInputs();
  }

  // ---------- Filters overlay ----------
  openFilters(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    this.showFilters(true);
  }

  closeFilters(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    this.showFilters(false);
    this.setPage(1);
    this.setRepeatMode("replace");
    this.hasMore = true;
    this.reloadPrograms();
    this.refreshScope("listings_search", { force: true });
  }

  closeFiltersOnOutside(event) {
    if (!this.hasFiltersOverlayTarget) return;
    // only when open
    if (this.filtersOverlayTarget.classList.contains("d-none")) return;
    const t = event?.target;
    if (!t || !t.closest) return;
    // ignore clicks inside the overlay
    if (t.closest("[data-listing-results-target='filtersOverlay']")) return;
    // ignore click on the open button
    if (t.closest("[data-action*='listing-results#openFilters']")) return;
    this.showFilters(false);
  }

  showFilters(show) {
    if (!this.hasFiltersOverlayTarget) return;
    this.filtersOverlayTarget.classList.toggle("d-none", !show);
  }

  // ---------- Source -> Program dependency ----------
  async sourceChanged(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    if (!this.hasSourceSelectTarget) return;
    const source = (this.sourceSelectTarget.value || "").trim();

    if (!source) {
      if (this.hasProgramSelectTarget) this.programSelectTarget.value = "";
      if (this.hasProgramGroupTarget) this.programGroupTarget.classList.add("d-none");
      this.applyProgramSelectedState();
      return;
    }

    if (!this.hasProgramsUrlValue || !this.programsUrlValue) return;
    if (!this.hasProgramSelectTarget) return;

    if (this.hasProgramGroupTarget) this.programGroupTarget.classList.remove("d-none");

    await this.reloadPrograms();
  }

  // ---------- Reload programs based on current source + filters ----------
  async reloadPrograms() {
    if (!this.hasProgramsUrlValue || !this.programsUrlValue) return;
    if (!this.hasProgramSelectTarget) return;
    if (!this.hasSourceSelectTarget) return;
    const source = (this.sourceSelectTarget.value || "").trim();
    if (!source) return;

    const payload = this._buildProgramsPayload();
    let items = [];
    try {
      const headers = { "Content-Type": "application/json", Accept: "application/json" };
      const apiToken = document.querySelector('meta[name="api-token"]')?.getAttribute("content");
      if (apiToken) headers["Authorization"] = `Bearer ${apiToken}`;

      const res = await fetch(this.programsUrlValue, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      items = Array.isArray(json.items) ? json.items : [];
    } catch (_) {
      items = [];
    }

    const previous = this.programSelectTarget.value;
    this.programSelectTarget.innerHTML = "";
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "Programme";
    this.programSelectTarget.appendChild(blank);
    items.forEach((it) => {
      const opt = document.createElement("option");
      opt.value = String(it.id);
      opt.textContent = String(it.label || it.name || it.id);
      this.programSelectTarget.appendChild(opt);
    });

    if (previous && items.some((it) => String(it.id) === String(previous))) {
      this.programSelectTarget.value = previous;
    } else {
      this.programSelectTarget.value = "";
    }

    if (this.programSelectTarget.value !== previous) {
      this._updatingPrograms = true;
      const form = this.hasSearchFormTarget ? this.searchFormTarget : null;
      if (form) this.dispatchFormChange(form);
      this._updatingPrograms = false;
    }

    this.applyProgramSelectedState();
  }

  _buildProgramsPayload() {
    const payload = {};

    [this.hasSearchFormTarget && this.searchFormTarget,
     this.hasProfileFormTarget && this.profileFormTarget]
      .filter(Boolean)
      .forEach((form) => {
        for (const [key, value] of new FormData(form).entries()) {
          if (key === "page" || key === "per") continue;
          if (key.includes("program_id")) continue;
          this._assignNested(payload, key, value);
        }
      });

    return payload;
  }

  _assignNested(target, key, value) {
    if (value === null || value === undefined || value === "") return;
    const isArray = /\[\]$/.test(key);
    const cleanKey = isArray ? key.replace(/\[\]$/, "") : key;
    const path = cleanKey.replace(/]/g, "").split("[").filter(Boolean);
    let obj = target;
    for (let i = 0; i < path.length; i++) {
      const part = path[i];
      if (i === path.length - 1) {
        if (isArray) {
          if (!Array.isArray(obj[part])) obj[part] = [];
          obj[part].push(value);
        } else {
          obj[part] = value;
        }
      } else {
        if (!obj[part] || typeof obj[part] !== "object") obj[part] = {};
        obj = obj[part];
      }
    }
  }

  _scheduleReloadPrograms() {
    if (this._programReloadTimer) clearTimeout(this._programReloadTimer);
    this._programReloadTimer = setTimeout(() => {
      this.reloadPrograms();
    }, this.debounceValue);
  }

  // ---------- Program selection: toggle location/image ----------
  programChanged(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    this.applyProgramSelectedState();
  }

  applyProgramSelectedState() {
    const selected = this.hasProgramSelectTarget && this.programSelectTarget.value !== "";
    this.programSelectedValue = selected;

    if (this.hasLocationGroupTarget) {
      this.locationGroupTarget.classList.toggle("d-none", selected);
    }

    if (this.hasItemsTarget) {
      this.itemsTarget.classList.toggle("simulator--program-selected", selected);
    }
  }

  // ---------- UI Actions ----------
  preview(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    const listingId = this.readListingId(event);
    if (!listingId) return;
    this.userHasSelected = true;
    this.selectListingId(listingId);
    this.setActiveListing(listingId);
    // Reset override flag when selecting a new listing (use DB data for initial load)
    this.setOverrideFlag(false);
    this.pendingPrefill = true;
    this.refreshScope("listing_details", { force: true });
  }

  details(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    const listingId = this.readListingId(event);
    if (!listingId) return;
    this.userHasSelected = true;
    this.selectListingId(listingId);
    this.setActiveListing(listingId);
    // Reset override flag when selecting a new listing (use DB data for initial load)
    this.setOverrideFlag(false);
    this.pendingPrefill = true;
    this.refreshScope("listing_details", { force: true });
    this.emitShowEdit(listingId);
  }

  // Réapplique la surbrillance du bien actif après le rendu des cards favoris.
  // Sans cela, le scénario 1 (#353) échoue : la liste des favoris est rendue de
  // façon asynchrone après l'ouverture de l'onglet, donc la card du bien déjà
  // sélectionné au Catalogue n'hérite jamais de la classe `.active`.
  handleFavoritesRendered() {
    if (this.activeListingId) this.setActiveListing(this.activeListingId);
  }

  // ---------- Active listing highlight ----------
  // Périmètre de sélection : la liste des résultats ET la carte « Bien épinglé »
  // (#295), qui vit hors du conteneur `items` mais doit participer à la même
  // logique de surbrillance (.active) qu'une carte de résultat.
  selectionScope() {
    if (this.hasListPanelTarget) return this.listPanelTarget;
    return this.element;
  }

  setActiveListing(listingId) {
    // Retire l'état actif de toutes les cards du simulateur. On opère sur
    // `this.element` (et non sur le seul périmètre Catalogue renvoyé par
    // `selectionScope()`) afin de couvrir aussi les cards de l'onglet
    // « Favoris » (.simulator-favorites-list) et la carte « Bien épinglé ».
    this.element.querySelectorAll(".simulator-card.active").forEach((el) => {
      el.classList.remove("active");
      try { el.setAttribute("aria-pressed", "false"); } catch (_) {}
    });
    // Legacy rows (catalogue uniquement)
    this.element.querySelectorAll(".border-bottom.active").forEach((el) => {
      el.classList.remove("active", "bg-light");
    });

    if (!listingId) {
      this.activeListingId = null;
      return;
    }
    this.activeListingId = String(listingId);
    // Hide the "no listing selected" empty state if it was visible (a listing is selected again)
    this.toggleDetailsEmptyState(false);
    // Active toutes les cards correspondantes dans tout le simulateur
    // (Catalogue ET onglet Favoris) pour que la surbrillance suive le bien
    // sélectionné simultanément dans les deux panneaux.
    const cards = this.element.querySelectorAll(`.simulator-card[data-listing-id="${this.cssEscape(this.activeListingId)}"]`);
    if (cards.length) {
      cards.forEach((card) => {
        card.classList.add("active");
        try { card.setAttribute("aria-pressed", "true"); } catch (_) {}
      });
      return;
    }
    // Repli sur les lignes legacy du périmètre Catalogue.
    const scope = this.selectionScope();
    if (!scope) return;
    const btn = scope.querySelector(`button[data-listing-id="${this.cssEscape(this.activeListingId)}"]`);
    if (!btn) return;
    const row = btn.closest(".border-bottom");
    if (row) {
      row.classList.add("active", "bg-light");
    }
  }

  // Called after a listings_search re-render when the previously selected
  // listing is no longer in the new result list. Resets selection state and
  // restores the details column to its initial server-rendered state (zeros /
  // "-" / empty placeholders) instead of re-fetching listing_details, which
  // would compute non-empty values via NewVirtualFromParams.
  clearDetailsSelection() {
    this.userHasSelected = false;
    this.activeListingId = null;
    this.pendingPrefill = false;

    const scope = this.selectionScope();
    if (scope) {
      scope.querySelectorAll(".simulator-card.active").forEach((el) => {
        el.classList.remove("active");
        try { el.setAttribute("aria-pressed", "false"); } catch (_) {}
      });
      scope.querySelectorAll(".border-bottom.active").forEach((el) => {
        el.classList.remove("active", "bg-light");
      });
    }

    this.selectListingId("");
    this.setOverrideFlag(false);
    this.clearSimulatorFormFields();

    this.restoreDetailsColumnInitialState();

    try {
      this.element.dispatchEvent(new CustomEvent("listing-results:selection-cleared", { bubbles: true }));
    } catch (_) {}
  }

  // Locate the details column inside the simulator panel.
  findDetailsColumn() {
    return this.element.querySelector(".simulator-column--details");
  }

  // Capture the initial inner HTML of the details column. Called once at
  // connect() time so it reflects the pristine server-rendered state.
  snapshotDetailsColumn() {
    const details = this.findDetailsColumn();
    if (!details) return;
    this.detailsColumnInitialHTML = details.innerHTML;
  }

  // Restore the details column to its initial server-rendered state (the same
  // visual state as the first page load before any listing_details response).
  // This avoids relying on a refetch of listing_details, which would recompute
  // values via NewVirtualFromParams instead of showing neutral placeholders.
  restoreDetailsColumnInitialState() {
    const details = this.findDetailsColumn();
    if (!details || this.detailsColumnInitialHTML == null) {
      this.toggleDetailsEmptyState(true);
      return;
    }
    details.innerHTML = this.detailsColumnInitialHTML;
    // Surface the spinners attached to each card to mirror the "waiting for
    // data" state of the initial page load.
    details.querySelectorAll("[data-api-sync-target='spinner']").forEach((s) => {
      s.classList.remove("d-none");
    });
    this.toggleDetailsEmptyState(true);
  }

  // The detailsEmptyState target may be re-created after innerHTML restore.
  // Use a direct DOM query rather than the Stimulus target getter to avoid
  // racing the target registry's MutationObserver.
  toggleDetailsEmptyState(show) {
    const banner = this.element.querySelector(
      "[data-listing-results-target='detailsEmptyState']"
    );
    if (!banner) return;
    banner.classList.toggle("d-none", !show);
  }

  // Clear all simulator[...] inputs in the details form so the user starts
  // from a blank slate. Hidden meta fields (listing_id, override_listing_data)
  // are handled separately and explicitly preserved.
  clearSimulatorFormFields() {
    const form = this.hasSimulatorFormTarget ? this.simulatorFormTarget : null;
    if (!form) return;
    const skipNames = new Set(["listing_id", "override_listing_data"]);
    const fields = form.querySelectorAll("input[name], select[name], textarea[name]");
    fields.forEach((el) => {
      const name = el.getAttribute("name") || "";
      if (skipNames.has(name)) return;
      if (!name.startsWith("simulator[")) return;
      if (el.type === "checkbox" || el.type === "radio") {
        el.checked = false;
        return;
      }
      el.value = "";
    });
  }

  // After a listings_search re-render, restore the previously selected listing
  // when still present, switch to an empty state when it has disappeared, or
  // fall back to auto-selecting the first listing when relevant.
  // Side-effects (clearDetailsSelection / auto-select) only fire when the
  // listing-browser is currently showing the Catalogue (list) panel, so a
  // background search refresh does not disturb users on the Simulation libre
  // tab.
  reconcileSelectionAfterSearch() {
    const browserMode = this.currentBrowserMode();

    if (this.userHasSelected && this.activeListingId) {
      const scope = this.selectionScope();
      if (!scope) return;
      const id = this.cssEscape(String(this.activeListingId));
      // On cherche dans tout le périmètre de sélection (et pas seulement
      // `itemsTarget`) pour que la carte « Bien épinglé », rendue hors de la
      // liste, soit reconnue comme toujours présente après un re-render (#295).
      const card = scope.querySelector(
        `.simulator-card[data-listing-id="${id}"], button[data-listing-id="${id}"]`
      );
      if (card) {
        this.setActiveListing(this.activeListingId);
      } else if (browserMode === "list") {
        this.clearDetailsSelection();
      }
      // browserMode !== "list": user is on Simulation libre, keep prefill / form / details column intact.
      return;
    }

    // Auto-sélection (non manuelle) : le surlignage doit survivre aux re-rendus
    // successifs de la liste. Après la première auto-sélection,
    // `pendingAutoSelectFirst` est retombé à `false` et `userHasSelected` reste
    // `false` ; or une recherche tardive (ex. reload des programmes déclenché
    // par un changement de fiscalité) re-rend `items` en mode `replace`, ce qui
    // efface la classe `.active` posée sur la carte. Sans restauration, la liste
    // se retrouve sans bien actif (cf. flaky spec « sélectionne automatiquement
    // le premier bien quand on clique sur l'onglet Catalogue »). On réapplique
    // donc le surlignage si la carte auto-sélectionnée est toujours présente,
    // sinon on ré-arme l'auto-sélection pour surligner le nouveau premier bien.
    if (browserMode === "list" && this.activeListingId) {
      const scope = this.selectionScope();
      const id = this.cssEscape(String(this.activeListingId));
      const card = scope && scope.querySelector(
        `.simulator-card[data-listing-id="${id}"], button[data-listing-id="${id}"]`
      );
      if (card) {
        this.setActiveListing(this.activeListingId);
        return;
      }
      // La carte auto-sélectionnée a disparu du nouveau résultat : on ré-arme
      // l'auto-sélection pour retomber sur le premier bien courant.
      this.pendingAutoSelectFirst = true;
    }

    if (this.pendingAutoSelectFirst && browserMode === "list") {
      this.selectFirstListingIfNeeded();
    }
  }

  // Returns the currently active mode of the listing-browser column
  // ("list", "edit", "favorites", "programs"), or null when unavailable.
  // Reads data-listing-browser-mode-value first, with a fallback on the
  // panel currently visible (the controller toggles d-none per panel).
  currentBrowserMode() {
    const browserEl = this.element.querySelector(
      "[data-controller~='listing-browser']"
    );
    if (!browserEl) return null;
    const mode = browserEl.dataset.listingBrowserModeValue;
    if (mode) return mode;
    const visiblePanel = browserEl.querySelector(
      "[data-listing-browser-target='panel']:not(.d-none)"
    );
    return visiblePanel?.dataset?.mode || null;
  }

  selectFirstListingIfNeeded(event) {
    try { event?.preventDefault?.(); } catch (_) {}
    // Only auto-select if user hasn't manually selected a listing
    if (this.userHasSelected) {
      this.pendingAutoSelectFirst = false;
      return;
    }
    if (!this.hasItemsTarget) {
      // List not ready, will retry when listings_search finishes
      this.pendingAutoSelectFirst = true;
      return;
    }
    // Bien à auto-sélectionner : le bien épinglé en priorité (#295), sinon le
    // premier résultat de recherche.
    const listingId = this.autoSelectListingId();
    if (!listingId) {
      // List is empty or not loaded yet, will retry when listings_search finishes
      this.pendingAutoSelectFirst = true;
      return;
    }
    // Successfully found a listing to select, clear pending flag
    this.pendingAutoSelectFirst = false;
    // Trigger preview for the listing (without setting userHasSelected)
    this.selectListingId(listingId);
    this.setActiveListing(listingId);
    // Reset override flag when selecting a new listing (use DB data for initial load)
    this.setOverrideFlag(false);
    this.pendingPrefill = true;
    this.refreshScope("listing_details", { force: true });
  }

  // Id à auto-sélectionner : le bien épinglé en priorité (#295), sinon le
  // premier bien (ou bouton legacy) des résultats. On privilégie l'id épinglé
  // mémorisé depuis `meta` (fiable), avec repli sur le DOM de la section
  // épinglée si besoin (ex. déclenchement via l'onglet Catalogue).
  autoSelectListingId() {
    const pinnedId = this.pinnedListingId || this.visiblePinnedListingId();
    if (pinnedId) return pinnedId;
    const firstEl = this.hasItemsTarget
      ? this.itemsTarget.querySelector("[data-listing-id]")
      : null;
    if (!firstEl) return null;
    const id = firstEl.dataset.listingId || firstEl.getAttribute("data-listing-id");
    return id ? String(id) : null;
  }

  // Id du bien épinglé quand la section est réellement affichée (non d-none) et
  // que sa carte porte un data-listing-id non vide (posé par remote-bind).
  visiblePinnedListingId() {
    if (!this.hasPinnedSectionTarget) return null;
    if (this.pinnedSectionTarget.classList.contains("d-none")) return null;
    const card = this.pinnedSectionTarget.querySelector(".simulator-card[data-listing-id]");
    if (!card) return null;
    const id = card.dataset.listingId || card.getAttribute("data-listing-id");
    return id ? String(id) : null;
  }

  // ---------- List pagination ----------
  loadMore() {
    // Ne jamais paginer avant que la page 1 ne soit chargée (évite le page=2
    // fantôme déclenché par la sentinelle visible sur une liste vide).
    if (!this.initialLoadDone || !this.hasMore || this.loadingSearch) return;
    const next = this.currentPage() + 1;
    this.setPage(next);
    this.setRepeatMode("append");
    this.refreshScope("listings_search", { force: true });
  }

  refreshScope(scope, { force } = {}) {
    try {
      this.element.dispatchEvent(new CustomEvent("api-sync:refresh", { bubbles: true, detail: { scope, force: Boolean(force) } }));
    } catch (_) {}
  }

  emitShowEdit(listingId) {
    // Dispatch inside the listing-browser subtree so its data-action catches it.
    const target = this.hasListPanelTarget ? this.listPanelTarget : this.element;
    try {
      target.dispatchEvent(new CustomEvent("listing-results:show-edit", { bubbles: true, detail: { listingId } }));
    } catch (_) {}
  }

  // ---------- Prefill form ----------
  applyListingInfoToSimulatorForm(listingInfo) {
    const form = this.hasSimulatorFormTarget ? this.simulatorFormTarget : null;
    if (!form || !listingInfo || typeof listingInfo !== "object") return;

    // Reset override flag to false during prefill (loading listing data from DB)
    this.setOverrideFlag(false);

    // Also set city_suggestion for UX when present
    if (listingInfo.city) {
      this.setFormField(form, "simulator[city_suggestion]", listingInfo.city, { normalizeNumberFormat: false });
    }

    Object.entries(listingInfo).forEach(([key, value]) => {
      // listing_info keys are aligned with form field names where possible
      const name = `simulator[${key}]`;
      this.setFormField(form, name, value, { normalizeNumberFormat: true });
    });
  }

  setFormField(form, name, value, { normalizeNumberFormat }) {
    const safeName = this.cssEscape(name);
    const el = form.querySelector(`[name="${safeName}"]`);
    if (!el) return;

    if (el.type === "checkbox") {
      el.checked = Boolean(value);
      return;
    }

    // Selects
    if (el.tagName === "SELECT") {
      el.value = value == null ? "" : String(value);
      return;
    }

    // Inputs / textareas
    el.value = value == null ? "" : String(value);

    if (normalizeNumberFormat && el.dataset && (el.dataset.controller || "").includes("number-format")) {
      try {
        el.dispatchEvent(new Event("number-format:normalize", { bubbles: true }));
      } catch (_) {}
    }
  }

  setRepeatMode(mode) {
    if (!this.hasItemsTarget) return;
    const m = String(mode || "replace").toLowerCase();
    this.itemsTarget.setAttribute("data-remote-repeat-mode", m === "append" ? "append" : "replace");
  }

  // Bien épinglé (#295) : affiche la carte épinglée quand `meta.pinned` est
  // présent, sinon le message d'indisponibilité quand `meta.pinned_status`
  // vaut "not_found". Les deux conteneurs restent masqués sans `source_id`.
  updatePinned(meta) {
    const pinned = meta && meta.pinned;
    const status = meta && meta.pinned_status;
    const hasPinnedZone = Boolean(pinned) || status === "not_found";
    // Id du bien épinglé courant, lu directement depuis `meta` (source fiable,
    // indépendante du timing de rendu DOM par remote-bind).
    this.pinnedListingId = pinned && pinned.id != null ? String(pinned.id) : null;
    if (this.hasPinnedSectionTarget) {
      this.pinnedSectionTarget.classList.toggle("d-none", !pinned);
    }
    if (this.hasPinnedNotFoundTarget) {
      this.pinnedNotFoundTarget.classList.toggle("d-none", status !== "not_found");
    }
    // Le séparateur n'apparaît qu'avec une zone épinglée (carte ou message).
    if (this.hasPinnedSeparatorTarget) {
      this.pinnedSeparatorTarget.classList.toggle("d-none", !hasPinnedZone);
    }
    // #295 : tant que l'utilisateur n'a pas fait de sélection manuelle et qu'on
    // est sur le Catalogue, le bien épinglé est auto-sélectionné en priorité —
    // y compris s'il arrive après qu'un premier résultat a déjà été
    // auto-sélectionné (réponses de recherche successives, race de timing).
    if (
      this.pinnedListingId &&
      !this.userHasSelected &&
      this.activeListingId !== this.pinnedListingId &&
      this.currentBrowserMode() === "list"
    ) {
      this.pendingAutoSelectFirst = false;
      this.selectListingId(this.pinnedListingId);
      this.setActiveListing(this.pinnedListingId);
      this.setOverrideFlag(false);
      this.pendingPrefill = true;
      this.refreshScope("listing_details", { force: true });
    }
  }

  updateCount(total) {
    if (!this.hasCountTarget) return;
    if (total == null) {
      this.countTarget.textContent = "";
      return;
    }
    this.countTarget.textContent = `${Number(total)} biens disponibles`;
  }

  bindResetOnInputs() {
    const forms = [];
    if (this.hasProfileFormTarget) forms.push(this.profileFormTarget);
    if (this.hasSearchFormTarget) forms.push(this.searchFormTarget);
    this.boundForms = forms;
    ["input", "change"].forEach((ev) => {
      forms.forEach((f) => f.addEventListener(ev, this.onPotentialFilterChange, true));
    });
  }

  unbindResetOnInputs() {
    if (!this.boundForms) return;
    ["input", "change"].forEach((ev) => {
      this.boundForms.forEach((f) => f.removeEventListener(ev, this.onPotentialFilterChange, true));
    });
    this.boundForms = null;
  }

  bindSimulatorInputs() {
    if (!this.hasSimulatorFormTarget) return;
    const form = this.simulatorFormTarget;
    ["input", "change"].forEach((ev) => {
      form.addEventListener(ev, this.onSimulatorInput, true);
    });
  }

  unbindSimulatorInputs() {
    if (!this.hasSimulatorFormTarget) return;
    const form = this.simulatorFormTarget;
    ["input", "change"].forEach((ev) => {
      form.removeEventListener(ev, this.onSimulatorInput, true);
    });
  }

  handleSimulatorInput(e) {
    // Ignore synthetic events from api-sync suggestion application or prefill
    if (e && (e.__investDetailsFromApply || (e.detail && e.detail.fromInvestApply))) return;
    // Ignore hidden fields (listing_id, override_listing_data)
    const name = e?.target?.getAttribute?.("name") || "";
    if (name === "listing_id" || name === "override_listing_data") return;
    // When user modifies custom listing form, set override flag to true
    this.setOverrideFlag(true);
  }

  handlePotentialFilterChange(e) {
    if (e && (e.__investDetailsFromApply || (e.detail && e.detail.fromInvestApply))) return;
    if (this._updatingPrograms) return;
    const name = e?.target?.getAttribute?.("name") || "";
    if (name === "page" || name === "per") return;
    this.setPage(1);
    this.setRepeatMode("replace");
    this.hasMore = true;

    const inSearchForm = this.hasSearchFormTarget && this.searchFormTarget.contains(e?.target);
    const inProfileForm = this.hasProfileFormTarget && this.profileFormTarget.contains(e?.target);

    // #434 : le select programme (`criterias[program_id]`) doit refléter les
    // programmes compatibles avec les critères courants. On relance donc la
    // requête `programs` (debounced) à *tout* changement de critère dans les
    // formulaires profil ou filtres, pas seulement sur `tax_program`. On exclut
    // le champ `program_id` lui-même (résultat du dropdown, pas un critère
    // filtrant) et `limit_to_source` (déjà géré par `sourceChanged`). La
    // pagination (`page`, `per`) est déjà court-circuitée en amont.
    if ((inSearchForm || inProfileForm) && !name.includes("program_id") && !name.includes("limit_to_source")) {
      this._scheduleReloadPrograms();
    }
  }

  // ---------- Infinite scroll ----------
  setupInfiniteScroll() {
    if (!this.hasSentinelTarget) return;
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.sentinelVisible = entry.isIntersecting;
        if (entry.isIntersecting) this.loadMore();
      });
    }, { root: null, rootMargin: "200px 0px", threshold: 0 });

    try { this.observer.observe(this.sentinelTarget); } catch (_) {}
  }

  // Vérifie si la sentinelle est réellement dans le viewport (avec la même
  // marge que le rootMargin de l'IntersectionObserver). Sert à décider d'une
  // pagination immédiate juste après le rendu, sans dépendre de l'état
  // potentiellement périmé de l'observer.
  isSentinelInView() {
    if (!this.hasSentinelTarget) return false;
    const el = this.sentinelTarget;
    // Élément non rendu / masqué (catalogue en mode Simulation libre : panneau
    // display:none) : offsetParent est null et getBoundingClientRect renvoie un
    // rect nul (0,0,0,0) qui passerait à tort le test « dans le viewport ». On
    // doit alors considérer la sentinelle comme NON visible pour ne pas
    // déclencher de loadMore (page=2) parasite hors du mode catalogue.
    if (el.offsetParent === null) return false;
    let rect;
    try { rect = el.getBoundingClientRect(); } catch (_) { return false; }
    if (rect.width === 0 && rect.height === 0) return false;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
    const margin = 200;
    return rect.top <= viewportH + margin && rect.bottom >= -margin;
  }

  // ---------- Helpers ----------
  readListingId(event) {
    const el = event?.currentTarget;
    const id = el && (el.dataset.listingId || el.getAttribute("data-listing-id"));
    return id || null;
  }

  selectListingId(listingId) {
    if (!this.hasListingIdTarget) return;
    try { this.listingIdTarget.value = String(listingId); } catch (_) {}
  }

  setOverrideFlag(value) {
    if (!this.hasOverrideFlagTarget) return;
    try { this.overrideFlagTarget.value = value ? "true" : "false"; } catch (_) {}
  }

  currentPage() {
    if (!this.hasPageFieldTarget) return 1;
    const n = Number(this.pageFieldTarget.value || 1);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  setPage(n) {
    if (!this.hasPageFieldTarget) return;
    try { this.pageFieldTarget.value = String(n); } catch (_) {}
  }

  handleRemoteSuccess(e) {
    const channel = e?.detail?.channel;
    const json = e?.detail?.json || {};
    if (channel === "listings_search") {
      const meta = json.meta || {};
      this.hasMore = Boolean(meta.has_more);
      this.updateCount(meta.total);
      this.lastSearchTotal = (meta.total != null) ? Number(meta.total) : null;
      this.updatePinned(meta);
      // La page 1 (ou une page suivante) vient d'être chargée : on autorise
      // désormais le scroll infini.
      this.initialLoadDone = true;
      // Wait for the DOM to be updated by remote-bind before inspecting items
      setTimeout(() => {
        this.reconcileSelectionAfterSearch();
        // Révèle la liste : le gabarit serveur (card « vide ») était masqué via
        // `d-none` au rendu initial pour éviter de l'afficher tant que la
        // recherche n'avait pas répondu. Une fois les items injectés par
        // remote-bind, on retire `d-none` (idempotent, jamais re-posé).
        if (this.hasItemsTarget) this.itemsTarget.classList.remove("d-none");
        // #400 : la liste à jour est révélée, on masque le spinner plein-zone.
        this.showListSpinner(false);
        // Si la page chargée ne remplit pas le viewport, la sentinelle reste
        // visible mais l'IntersectionObserver ne se redéclenche pas (l'état
        // d'intersection ne change pas) : on poursuit donc la pagination pour
        // conserver le comportement de scroll infini. On vérifie la géométrie
        // RÉELLE de la sentinelle (pas le flag sentinelVisible, qui peut être
        // périmé : l'observer l'a mis à true en voyant la sentinelle dans la
        // liste encore vide, et son callback asynchrone n'est pas forcément
        // repassé après le rendu des items). Sans ça, page=2 partait au
        // chargement sans scroll (cf. #272).
        if (this.hasMore && !this.loadingSearch && this.isSentinelInView()) {
          this.loadMore();
        }
      }, 0);
      return;
    }
    if (channel === "listing_details") {
      if (!this.pendingPrefill) return;
      this.pendingPrefill = false;
      this.applyListingInfoToSimulatorForm(json?.values_for_form || {});
    }
  }

  dispatchFormChange(form) {
    if (!form) return;
    try { form.dispatchEvent(new Event("change", { bubbles: true })); } catch (_) {}
    try { form.dispatchEvent(new Event("input", { bubbles: true })); } catch (_) {}
  }

  cssEscape(str) {
    try {
      if (typeof CSS !== "undefined" && CSS.escape) return CSS.escape(str);
    } catch (_) {}
    // Minimal escape fallback for attribute selectors
    return String(str).replace(/["\\]/g, "\\$&");
  }

  handleRemoteStart(e) {
    const channel = e?.detail?.channel;
    if (channel === "listings_search") {
      this.loadingSearch = true;
      // Hide "X biens disponibles" and show spinner instead
      if (this.hasCountTarget) this.countTarget.classList.add("d-none");
      if (this.hasCountSpinnerTarget) this.countSpinnerTarget.classList.remove("d-none");
      this.showLoadingMessage(true);
      this.showEmptyMessage(false);
      // #400 : en mode `replace` (changement de filtre / critère de profil), on
      // masque immédiatement la liste périmée et on affiche un spinner à sa
      // place jusqu'au retour des nouveaux résultats. En mode `append` (scroll
      // infini), on ne touche pas à la liste : on continue d'ajouter des pages
      // sous l'existant (le petit indicateur de la sentinelle suffit).
      if (this.currentRepeatMode() === "replace") {
        this.showListSpinner(true);
      }
    }
  }

  // Mode de répétition courant de la liste de résultats ("replace" ou
  // "append"), lu depuis l'attribut posé par setRepeatMode(). Par défaut
  // "replace".
  currentRepeatMode() {
    if (!this.hasItemsTarget) return "replace";
    const mode = (this.itemsTarget.getAttribute("data-remote-repeat-mode") || "replace").toLowerCase();
    return mode === "append" ? "append" : "replace";
  }

  // Affiche/masque le spinner plein-zone qui remplace la liste pendant un
  // rechargement complet (#400). Quand on l'affiche, on masque la liste pour
  // qu'elle ne montre pas les anciennes valeurs ; quand on le masque, on laisse
  // handleRemoteSuccess révéler la liste à jour.
  showListSpinner(show) {
    if (this.hasListSpinnerTarget) {
      this.listSpinnerTarget.classList.toggle("d-none", !show);
    }
    if (show && this.hasItemsTarget) {
      // Mémorise si la liste était déjà masquée (rendu initial : gabarit
      // serveur encore en `d-none`). On ne la révélera pas en cas d'échec de
      // recherche (handleRemoteFinish), pour ne pas exposer la card « vide ».
      this._itemsHiddenBeforeSpinner = this.itemsTarget.classList.contains("d-none");
      this.itemsTarget.classList.add("d-none");
    }
  }

  handleRemoteFinish(e) {
    const channel = e?.detail?.channel;
    if (channel === "listings_search") {
      this.loadingSearch = false;
      if (this.hasCountSpinnerTarget) this.countSpinnerTarget.classList.add("d-none");
      if (this.hasCountTarget) this.countTarget.classList.remove("d-none");
      this.showLoadingMessage(false);
      this.showEmptyMessage(this.shouldShowEmptyMessage());
      // #400 : filet de sécurité — masquer le spinner plein-zone même si la
      // recherche échoue (handleRemoteSuccess n'est pas appelé dans ce cas).
      // En cas de succès, le spinner est déjà masqué par handleRemoteSuccess et
      // la liste a été révélée, donc on n'a rien à re-masquer ici.
      if (this.hasListSpinnerTarget && !this.listSpinnerTarget.classList.contains("d-none")) {
        this.listSpinnerTarget.classList.add("d-none");
        // On ne réaffiche la liste que si elle était visible avant le spinner
        // (sinon, au tout premier chargement raté, on exposerait le gabarit
        // serveur encore vide).
        if (this.hasItemsTarget && this._itemsHiddenBeforeSpinner === false) {
          this.itemsTarget.classList.remove("d-none");
        }
      }
    }
  }

  shouldShowEmptyMessage() {
    if (this.lastSearchTotal != null) return this.lastSearchTotal === 0;
    if (this.hasItemsTarget) {
      return !this.itemsTarget.querySelector("[data-listing-id]");
    }
    return false;
  }

  showLoadingMessage(show) {
    if (!this.hasLoadingMessageTarget) return;
    this.loadingMessageTarget.classList.toggle("d-none", !show);
  }

  showEmptyMessage(show) {
    if (!this.hasEmptyMessageTarget) return;
    this.emptyMessageTarget.classList.toggle("d-none", !show);
  }

}

